import { BalanceType, BetSlipFragment, Platforms, StakeOrderFragment } from "../graphql/core-types"
import { calculatePotentialPayout, getFundingTotals, getPayoutAmounts } from "./bet-line-helpers"

// staking, ported from plc-app: src/lib/helpers/stake-funding.ts, get-stakers-data.ts and get-total-potential-prize-pool.ts.
// stakers back the streamer's bet with their own money and win alongside it

type Funding = StakeOrderFragment['fundingSource']

const isPredictionCreditFunding = (funding: Funding): boolean =>
  funding?.some((source) => source?.wallet === BalanceType.PREDICTION_BET_CREDITS) ?? false

// cash plus prediction credits, what plc-app counts as the amount staked
const stakeFundingAmount = (funding: Funding): number =>
  (funding ?? []).reduce((total, source) =>
    source && (source.wallet === BalanceType.PL_BALANCE || source.wallet === BalanceType.PREDICTION_BET_CREDITS)
      ? total + Number(source.amount ?? 0)
      : total, 0)

// maximumMultiplier is stored in thousandths (2500 = 2.5x)
const roundDown = (value: number) => Math.floor(value * 100) / 100

// a credit funded stake only wins the profit, the credits themselves aren't paid back
const stakeCreditCashPayout = (amount: number, maximumMultiplier: number): number =>
  roundDown(amount * Math.max(maximumMultiplier / 1000 - 1, 0))

const getOrders = (betSlip: BetSlipFragment): StakeOrderFragment[] =>
  (betSlip.stakeOffer?.orders ?? []).filter((order): order is StakeOrderFragment => Boolean(order))

export type Staker = {
  id: string
  name: string
  avatarUrl: string | null
}

// one entry per staker in the order they staked, someone staking twice still shows once
export const getStakers = (betSlip: BetSlipFragment): Staker[] => {
  const orders = [...getOrders(betSlip)].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
  const stakers = new Map<string, Staker>()
  orders.forEach((order) => {
    if (stakers.has(order.user.id)) return
    const twitch = order.user.platformIdentifiers?.find((identifier) => identifier?.platform === Platforms.TWITCH)
    stakers.set(order.user.id, {
      id: order.user.id,
      name: twitch?.gamerTag || order.user.loungeUsername || 'Player',
      avatarUrl: twitch?.metadata?.twitchProfile?.profileImageUrl ?? null,
    })
  })
  return Array.from(stakers.values())
}

export const getTotalStaked = (betSlip: BetSlipFragment): number =>
  getOrders(betSlip).reduce((total, order) => total + stakeFundingAmount(order.fundingSource), 0)

// what every staker stands to win, the staker half of plc-app's getTotalPotentialPrizePool
const getStakersPotentialPayout = (betSlip: BetSlipFragment): number => {
  const maximumMultiplier = betSlip.stakeOffer?.maximumMultiplier ?? 0
  return getOrders(betSlip).reduce((total, order) => {
    if (isPredictionCreditFunding(order.fundingSource)) {
      return total + stakeCreditCashPayout(stakeFundingAmount(order.fundingSource), maximumMultiplier)
    }
    const cashStaked = Number(order.fundingSource?.find((source) => source?.wallet === BalanceType.PL_BALANCE)?.amount ?? 0)
    return total + cashStaked * roundDown(maximumMultiplier / 1000)
  }, 0)
}

// what stakers were actually paid once the bet settled
const getStakersPayout = (betSlip: BetSlipFragment): number =>
  getOrders(betSlip).reduce((total, order) =>
    total + (order.payout?.payouts ?? []).reduce((sum, payout) => sum + Number(payout?.amount ?? 0), 0), 0)

const getStakersCashStaked = (betSlip: BetSlipFragment): number =>
  getOrders(betSlip).reduce((total, order) =>
    total + Number(order.fundingSource?.find((source) => source?.wallet === BalanceType.PL_BALANCE)?.amount ?? 0), 0)

// the streamer and their stakers together: everything wagered, and the profit it stands to make (or made once settled).
// profit only takes off the cash staked, credits aren't paid back so the payouts already leave them out
export const getCombinedTotals = (betSlip: BetSlipFragment, isResolved: boolean): { wagered: number, profit: number } => {
  const { plBalance, plCredits } = getFundingTotals(betSlip.betItems || [])
  const bettorPayout = getPayoutAmounts(betSlip)
  const payout = isResolved
    ? bettorPayout.dollars + bettorPayout.credits + getStakersPayout(betSlip)
    : Number(calculatePotentialPayout(betSlip)) + getStakersPotentialPayout(betSlip)
  return {
    wagered: plBalance + plCredits + getTotalStaked(betSlip),
    profit: payout - plBalance - getStakersCashStaked(betSlip),
  }
}

// what the stakers won on a settled bet. their payouts can land after the bet settles (and the socket's slip has
// none), so until they're in it's worked out from the multiplier the bet actually hit, capped at the offer's maximum
export const getStakersWinnings = (betSlip: BetSlipFragment): number => {
  const recordedPayout = getStakersPayout(betSlip)
  if (recordedPayout > 0) return recordedPayout

  const { plBalance, plCredits } = getFundingTotals(betSlip.betItems || [])
  const wagered = plBalance + plCredits
  if (wagered <= 0) return 0
  // the payout leaves out credits staked, adding them back gives the multiplier on the whole stake
  const bettorPayout = getPayoutAmounts(betSlip)
  const maximumMultiplier = (betSlip.stakeOffer?.maximumMultiplier ?? 0) / 1000
  const achievedMultiplier = (bettorPayout.dollars + bettorPayout.credits + plCredits) / wagered
  const multiplier = roundDown(maximumMultiplier > 0 ? Math.min(achievedMultiplier, maximumMultiplier) : achievedMultiplier)

  return getOrders(betSlip).reduce((total, order) => {
    const staked = stakeFundingAmount(order.fundingSource)
    return total + (isPredictionCreditFunding(order.fundingSource)
      ? roundDown(staked * Math.max(multiplier - 1, 0))
      : staked * multiplier)
  }, 0)
}
